import {
  Alert,
  type AlertColor,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import type { StyleObj } from '../../@types';
import type { CryptoWithdrawal } from '../../@types/api';
import { zeroXSubmissionErrorSchema } from '../../schema/zeroXSubmissionError';
import { isWaitingForLiquidity, WITHDRAWAL_STATE_LABELS } from '../../helpers/zeroXWithdrawalDisplay';

export {
  getZeroXStatusNotice,
  isWaitingForLiquidity,
  WITHDRAWAL_STATE_LABELS,
} from '../../helpers/zeroXWithdrawalDisplay';

const NOT_AVAILABLE = 'Not available';

/** Frozen withdrawal facts as label/value pairs, shared by the dialog and exports. */
export const getWithdrawalFacts = (withdrawal: CryptoWithdrawal): { label: string; value: string }[] => {
  const asset = (amount: string | null | undefined) =>
    amount == null ? NOT_AVAILABLE : `${amount} ${withdrawal.assetCurrency}`;

  const parsed = zeroXSubmissionErrorSchema.safeParse(withdrawal.submissionError);
  const diagnostic = parsed.success ? parsed.data : null;

  return [
    { label: 'Account debit', value: `${withdrawal.debit} ${withdrawal.accountCurrency}` },
    { label: 'Gross crypto amount', value: asset(withdrawal.assetAmount) },
    { label: 'Estimated network fee', value: asset(withdrawal.estimatedNetworkFee) },
    { label: 'Estimated crypto received', value: asset(withdrawal.estimatedReceived) },
    { label: 'Actual fee', value: asset(withdrawal.actualFee) },
    { label: 'Destination address', value: withdrawal.address },
    { label: 'Destination tag', value: withdrawal.tag ?? 'Not required' },
    { label: 'Provider withdrawal ID', value: withdrawal.providerWithdrawalId ?? NOT_AVAILABLE },
    { label: 'Transaction hash', value: withdrawal.hash ?? NOT_AVAILABLE },
    { label: 'Failure reason', value: withdrawal.failureReason ?? NOT_AVAILABLE },
    ...(diagnostic
      ? [
          { label: 'Submission error category', value: diagnostic.code },
          { label: 'Provider error code', value: diagnostic.providerCode ?? NOT_AVAILABLE },
          {
            label: 'Provider HTTP status',
            value: diagnostic.httpStatus === null ? NOT_AVAILABLE : String(diagnostic.httpStatus),
          },
          { label: 'Submission error observed', value: diagnostic.observedAt },
        ]
      : []),
  ];
};

const styles: StyleObj = {
  facts: {
    display: 'grid',
    gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
    gap: 1.5,
    my: 2,
    overflowWrap: 'anywhere',
  },
  fact: {
    minWidth: 0,
    p: 1.5,
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
  },
  label: { fontWeight: 600 },
  value: { m: 0, wordBreak: 'break-word' },
  liquidity: { mt: 1 },
};

const getStateSeverity = (state: CryptoWithdrawal['state']): AlertColor => {
  if (state === 'confirmed') return 'success';
  if (state === 'aborted' || state === 'rejected') return 'error';
  return 'warning';
};

const ZeroXWithdrawalDetails = ({ withdrawal }: { withdrawal?: CryptoWithdrawal }) => {
  const [open, setOpen] = useState(false);

  if (!withdrawal) return null;

  const facts = getWithdrawalFacts(withdrawal);

  return (
    <>
      <Button size='small' onClick={() => setOpen(true)}>
        Withdrawal details
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth='md'
        aria-labelledby='zero-x-withdrawal-title'
      >
        <DialogTitle id='zero-x-withdrawal-title'>0xProcessing withdrawal</DialogTitle>
        <DialogContent>
          <Alert severity={getStateSeverity(withdrawal.state)}>{WITHDRAWAL_STATE_LABELS[withdrawal.state]}</Alert>
          {isWaitingForLiquidity(withdrawal) && (
            <Alert severity='warning' sx={styles.liquidity}>
              The merchant's 0xProcessing balance was too low for this amount when it was requested. Top up the merchant
              balance, then approve.
            </Alert>
          )}
          {withdrawal.state === 'unknown' &&
            zeroXSubmissionErrorSchema.safeParse(withdrawal.submissionError).success && (
              <Typography variant='body2'>The recorded API error does not establish a final payout outcome.</Typography>
            )}
          <Stack component='dl' sx={styles.facts}>
            {facts.map(({ label, value }) => (
              <Stack key={label} component='div' sx={styles.fact}>
                <Typography component='dt' variant='body2' sx={styles.label}>
                  {label}
                </Typography>
                <Typography component='dd' variant='body2' sx={styles.value}>
                  {value}
                </Typography>
              </Stack>
            ))}
          </Stack>
          <Typography variant='body2'>
            Estimates were frozen at confirmation. Actual fee and transaction hash appear after provider verification.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ZeroXWithdrawalDetails;
